import React, { useEffect, useState, useRef } from 'react';
import Layout from './layout';
import { getTopics, getTopicMessages, sendMessage } from './api/chatApi';
import ChatSendMessageModal from './ChatSendMessageModal';
import apiClient from './api/apiClient';

function ChatPage() {
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [involved, setInvolved] = useState(false);
  const messagesEndRef = useRef(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [refreshCountdown, setRefreshCountdown] = useState(5);
  const refreshIntervalRef = useRef(null);

  useEffect(() => {
    fetchTopics(1, false);
  }, []);

  // Auto-refresh messages every 5 seconds when a topic is selected
  useEffect(() => {
    if (!selectedTopic) {
      clearInterval(refreshIntervalRef.current);
      setRefreshCountdown(5);
      return;
    }
    setRefreshCountdown(5);
    refreshIntervalRef.current = setInterval(() => {
      setRefreshCountdown(prev => {
        if (prev === 1) {
          fetchMessages({ id: selectedTopic });
          return 5;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(refreshIntervalRef.current);
  }, [selectedTopic]);

  const fetchTopics = async (pageToFetch = 1, append = false) => {
    setLoadingTopics(true);
    try {
      const data = await getTopics(`?page=${pageToFetch}`);
      const newTopics = Array.isArray(data) ? data : data.topics || [];
      if (append) {
        setTopics(prev => [...prev, ...newTopics]);
      } else {
        setTopics(newTopics);
      }
      setHasMore(newTopics.length > 0);
    } catch {
      if (!append) setTopics([]);
      setHasMore(false);
    } finally {
      setLoadingTopics(false);
    }
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchTopics(nextPage, true);
  };

  const fetchMessages = async (topic) => {
    setLoadingMessages(true);
    setSelectedTopic(topic.id);
    try {
      const data = await getTopicMessages(topic.id);
      const msgs = Array.isArray(data) ? data : data.messages || [];
      setMessages(msgs);
      setInvolved(msgs.some(m => m.involved));
    } catch {
      setMessages([]);
      setInvolved(false);
    } finally {
      setLoadingMessages(false);
      setTimeout(() => {
        if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!messageText.trim() || !selectedTopic) return;
    setSending(true);
    try {
      await sendMessage(selectedTopic, messageText );
      setMessageText('');
      // Do NOT refresh messages here, just append or rely on auto-refresh
      // fetchMessages(selectedTopic); // <-- REMOVE THIS LINE
    } catch {
      // Optionally show error
    } finally {
      setSending(false);
    }
  };

  // Send a message to a topic (from modal)
  const handleModalSend = async ({ title, message, receiver_student_id, receiver_trainer_id }) => {
    setSendLoading(true);
    try {
      await apiClient('/topics', {
        method: 'POST',
        body: { title, message, receiver_student_id, receiver_trainer_id },
      });
      setShowSendModal(false);
      // Refresh topics and messages after sending from modal
      fetchTopics(1, false);
      if (selectedTopic) fetchMessages({ id: selectedTopic });
    } catch {
      // Optionally show error
    } finally {
      setSendLoading(false);
    }
  };

  return (
    <Layout>
      <div className="container p-4">
        <div className="row">
          {/* Topics List */}
          <div className="col-md-4 border-end" style={{ minHeight: 500 }}>
            <h4>Topics</h4>
            <button className="btn btn-success mb-3" onClick={() => setShowSendModal(true)}>Send a Message</button>
            {loadingTopics && topics.length === 0 ? (
              <div>Loading topics...</div>
            ) : (
              <>
                <ul className="list-group">
                  {topics.length === 0 && <li className="list-group-item text-muted">No topics found.</li>}
                  {topics.map((topic) => (
                    <li
                      key={topic.id}
                      className={`my-2 hover list-group-item pointer${selectedTopic === topic.id ? ' active' : ''}`}
                      onClick={() => fetchMessages(topic)}
                    >
                      <div className="fw-bold">{topic.title}</div>
                      <div className="small text-muted">{topic.sender.full_name} → {topic.recipient.full_name}</div>
                    </li>
                  ))}
                </ul>
                {hasMore && (
                  <div className="text-center my-2">
                    <button className="btn btn-outline-primary" onClick={handleLoadMore} disabled={loadingTopics}>
                      {loadingTopics ? 'Loading...' : 'Load More'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Messages Section */}
          <div className="col-md-8 d-flex flex-column" style={{ minHeight: 500 }}>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h4 className="mb-0">Messages</h4>
              {selectedTopic && (
                <button className="btn btn-outline-secondary end-0" onClick={() => fetchMessages({ id: selectedTopic })}>
                  Refresh
                </button>
              )}
            </div>
            {selectedTopic ? (
              <>
                <div className="flex-grow-1 overflow-auto mb-3" style={{ maxHeight: 400, minHeight: 200, background: '#f8fafc', borderRadius: 8, padding: 16 }}>
                  {messages.length === 0 ? (
                    <div className="text-muted">No messages yet.</div>
                  ) : (
                    messages.map((msg) => {
                      const isCurrentUser = msg.sender?.full_name === 'TVB Admin';
                      return (
                        <div key={msg.id} className={`mb-2 ${isCurrentUser ? 'text-end' : ''}`}>
                          <div className={`fw-bold ${isCurrentUser ? 'text-end' : ''}`} style={{ fontSize: '0.95em' }}>
                            {msg.sender?.full_name || msg.sender?.email || 'User'}
                          </div>
                          <div 
                            className={`p-2 rounded shadow-sm ${isCurrentUser ? 'bg-primary text-white ms-auto' : 'bg-white'}`} 
                            style={{ 
                              display: 'inline-block', 
                              maxWidth: '80%',
                              textAlign: isCurrentUser ? 'right' : 'left'
                            }}
                          >
                            {msg.content}
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>
                <form className="d-flex gap-2" onSubmit={handleSend} style={{ borderTop: '1px solid #eee', paddingTop: 8 }}>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={messageText}
                    onChange={e => setMessageText(e.target.value)}
                    placeholder="Type your message..."
                    disabled={sending || !involved}
                  />
                  <button className="btn btn-primary" type="submit" disabled={sending || !messageText.trim() || !involved} style={{ minWidth: 80 }}>
                    {sending ? 'Sending...' : 'Send'}
                  </button>
                </form>
              </>
            ) : (
              <div className="text-muted mt-4">Select a topic to view messages.</div>
            )}
          </div>
        </div>
      </div>
      <ChatSendMessageModal
        show={showSendModal}
        onClose={() => setShowSendModal(false)}
        onSend={handleModalSend}
        loading={sendLoading}
      />
    </Layout>
  );
}

export default ChatPage; 